import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { connectAdmin } from '../api/apiConfig';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

function fmt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-CL');
}

const ConnectRegistros = () => {
  const { user } = useUser();
  const [tab, setTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [actividad, setActividad] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ejecutivo, setEjecutivo] = useState('');
  const [origen, setOrigen] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState(null);

  const filters = useMemo(
    () => ({
      ejecutivo: ejecutivo.trim() || undefined,
      origen: origen.trim() || undefined,
      fecha_desde: fechaDesde || undefined,
      fecha_hasta: fechaHasta || undefined,
      q: q.trim() || undefined,
    }),
    [ejecutivo, origen, fechaDesde, fechaHasta, q],
  );

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      if (tab === 'leads') {
        const result = await connectAdmin(user, { accion: 'listar_leads', ...filters });
        setLeads(Array.isArray(result?.data) ? result.data : []);
      } else {
        const result = await connectAdmin(user, { accion: 'listar_actividad', ...filters });
        setActividad(Array.isArray(result?.data) ? result.data : []);
      }
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar los registros.');
      if (tab === 'leads') setLeads([]);
      else setActividad([]);
    } finally {
      setLoading(false);
    }
  }, [user, tab, filters]);

  useEffect(() => {
    load();
  }, [user, tab]); // filtros se aplican con el botón

  const ejecutivos = useMemo(() => {
    const set = new Set();
    for (const r of leads) if (r.ejecutivo) set.add(r.ejecutivo);
    for (const r of actividad) if (r.ejecutivo) set.add(r.ejecutivo);
    return [...set].sort((a, b) => a.localeCompare(b, 'es'));
  }, [leads, actividad]);

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-800">Connect — Registros</h1>
        <p className="text-gray-600">Leads y actividad capturados desde las tarjetas</p>
        <Link to="/panel-marketing/connect-tarjetas" className="btn-primary text-sm py-2 px-3" style={{ textDecoration: 'none' }}>
          Ver tarjetas
        </Link>
      </div>

      <div className="seccion-datos">
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            className={`px-3 py-2 text-sm rounded-lg border ${tab === 'leads' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-gray-700'}`}
            onClick={() => setTab('leads')}
          >
            Leads
          </button>
          <button
            type="button"
            className={`px-3 py-2 text-sm rounded-lg border ${tab === 'actividad' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-gray-700'}`}
            onClick={() => setTab('actividad')}
          >
            Actividad
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <input className="px-3 py-2 border rounded-lg text-sm" placeholder="Buscar nombre / empresa" value={q} onChange={(e) => setQ(e.target.value)} />
          <input className="px-3 py-2 border rounded-lg text-sm" list="connect-ejecs" placeholder="Ejecutivo" value={ejecutivo} onChange={(e) => setEjecutivo(e.target.value)} />
          <datalist id="connect-ejecs">
            {ejecutivos.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
          <input className="px-3 py-2 border rounded-lg text-sm" placeholder="Origen (src)" value={origen} onChange={(e) => setOrigen(e.target.value)} />
          <input type="date" className="px-3 py-2 border rounded-lg text-sm" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
          <input type="date" className="px-3 py-2 border rounded-lg text-sm" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
          <button type="button" className="btn-primary text-sm py-2 px-3" disabled={loading} onClick={load}>
            {loading ? 'Cargando…' : 'Aplicar filtros'}
          </button>
        </div>

        {error && <div className="mb-4 text-sm rounded px-3 py-2 bg-red-50 text-red-700 border border-red-200">{error}</div>}

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-8">
            <AiOutlineLoading3Quarters className="animate-spin" /> Cargando…
          </div>
        ) : tab === 'leads' ? (
          <div className="overflow-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Ingreso</th>
                  <th className="text-left px-3 py-2">Lead</th>
                  <th className="text-left px-3 py-2">Empresa</th>
                  <th className="text-left px-3 py-2">WhatsApp</th>
                  <th className="text-left px-3 py-2">Correo</th>
                  <th className="text-left px-3 py-2">Necesidad</th>
                  <th className="text-left px-3 py-2">Ejecutivo</th>
                  <th className="text-left px-3 py-2">Origen</th>
                  <th className="text-left px-3 py-2">Veces</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-gray-400">
                      Sin leads todavía.
                    </td>
                  </tr>
                )}
                {leads.map((r) => (
                  <tr key={r.lead_id} className="border-t border-gray-100">
                    <td className="px-3 py-2 whitespace-nowrap">{fmt(r.ultimo_ingreso || r.creado_en)}</td>
                    <td className="px-3 py-2 font-semibold text-gray-800">{r.nombre}</td>
                    <td className="px-3 py-2">{r.empresa}</td>
                    <td className="px-3 py-2">{r.whatsapp}</td>
                    <td className="px-3 py-2">{r.email || '—'}</td>
                    <td className="px-3 py-2">{r.necesidad || '—'}</td>
                    <td className="px-3 py-2">{r.ejecutivo || '—'}</td>
                    <td className="px-3 py-2">{r.origen || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`font-semibold ${Number(r.veces_capturado) > 1 ? 'text-amber-700' : 'text-gray-700'}`}>
                        {r.veces_capturado || 1}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">Ejecutivo</th>
                  <th className="text-left px-3 py-2">Origen</th>
                  <th className="text-left px-3 py-2">Lead</th>
                  <th className="text-left px-3 py-2">URL</th>
                </tr>
              </thead>
              <tbody>
                {actividad.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                      Sin actividad todavía.
                    </td>
                  </tr>
                )}
                {actividad.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 whitespace-nowrap">{fmt(r.creado_en)}</td>
                    <td className="px-3 py-2 font-semibold">{r.tipo}</td>
                    <td className="px-3 py-2">{r.ejecutivo || '—'}</td>
                    <td className="px-3 py-2">{r.origen || '—'}</td>
                    <td className="px-3 py-2">{r.lead_id || '—'}</td>
                    <td className="px-3 py-2 max-w-xs truncate" title={r.url || ''}>
                      {r.url || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectRegistros;
